import Logger from 'customer-flows/utils/logger'
import type IntlService from 'ember-intl/services/intl'

type TranslationOverrides = Record<string, string>

interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

interface InvalidOverride {
  value: string
  errors: string[]
  warnings: string[]
}

interface FilterValidTranslationsResult {
  validOverrides: TranslationOverrides
  invalidOverrides: Record<string, InvalidOverride>
}

function toTranslationOverrides(languageOverrides: LanguageOverride[]): TranslationOverrides {
  return languageOverrides.reduce((overrides, { key, value }) => Object.assign(overrides, { [key]: value }), {})
}

export function validateAndAddLanguageOverrides(
  languageOverrides: LanguageOverride[] | undefined,
  intl: IntlService,
  locale: string
): void {
  if (!languageOverrides?.length) return

  const overrides = toTranslationOverrides(languageOverrides)
  const { validOverrides, invalidOverrides } = filterValidTranslations(overrides)

  if (Object.keys(invalidOverrides).length > 0) {
    Logger.warn('Invalid language overrides detected and filtered out', {
      locale,
      invalidOverrides,
      invalidCount: Object.keys(invalidOverrides).length,
      totalCount: Object.keys(overrides).length
    })
  }

  try {
    if (Object.keys(validOverrides).length > 0) {
      intl.addTranslations(locale, validOverrides)
    }
  } catch (error) {
    Logger.warn('Failed to load the language overrides', {
      ...(error as Record<string, unknown>),
      locale,
      validOverrides,
      validCount: Object.keys(validOverrides).length
    })
  }
}

function checkUnmatchedBraces(message: string): string[] {
  const openBraces = (message.match(/\{/g) || []).length
  const closeBraces = (message.match(/\}/g) || []).length
  if (openBraces === closeBraces) return []
  return [`Unmatched braces will break ember-intl (${openBraces} open, ${closeBraces} close)`]
}

function checkPluralMissingOther(message: string): string[] {
  if (!message.includes('plural,') || message.includes('other')) return []
  return ["Plural format missing required 'other' case (ember-intl requirement)"]
}

function checkPluralMissingComma(message: string): string[] {
  if (!message.includes('{count, plural') || message.includes('{count, plural,')) return []
  return ["Missing comma after 'plural' will break ember-intl"]
}

// Only checks variables that are clearly not part of an ICU plural/select expression
function checkInvalidVariableNames(message: string): string[] {
  const potentialVariables = message.match(/\{([a-zA-Z_][a-zA-Z0-9_-]*)\}/g) || []
  const errors: string[] = []
  for (const match of potentialVariables) {
    const content = match.slice(1, -1)
    if (content.includes('-')) {
      errors.push(`Invalid variable name '${content}' will break ember-intl (use underscore instead of dash)`)
    }
  }
  return errors
}

function checkInvalidEscapeSequences(message: string): string[] {
  if (!message.includes("\\'") && !message.includes('\\"')) return []
  return ["Invalid escape sequences will break ember-intl (use '' for single quote)"]
}

// {{count}} and other variables are allowed inside ICU plural/select expressions only
function checkNestedBraces(message: string): string[] {
  if (!message.includes('{{')) return []
  const isInsideICU = message.includes('plural,') || message.includes('select,')
  if (isInsideICU) return []
  return ['Nested braces not supported by ember-intl']
}

function checkHtmlContent(message: string): string[] {
  if (!message.includes('<') || !message.includes('>')) return []
  return ['HTML detected. Ensure proper handling with htmlSafe() if needed']
}

function checkXssVulnerability(message: string): string[] {
  if (!message.includes('<script') && !message.includes('javascript:')) return []
  return ['Potential XSS vulnerability detected']
}

const ERROR_CHECKS = [
  checkUnmatchedBraces,
  checkPluralMissingOther,
  checkPluralMissingComma,
  checkInvalidVariableNames,
  checkInvalidEscapeSequences,
  checkNestedBraces,
  checkXssVulnerability
]

const WARNING_CHECKS = [checkHtmlContent]

export function validateTranslationMessage(message: unknown, _key: string): ValidationResult {
  if (typeof message !== 'string') {
    return { isValid: true, errors: [], warnings: [] } // Skip non-string values
  }

  try {
    const errors = ERROR_CHECKS.flatMap((check) => check(message))
    const warnings = WARNING_CHECKS.flatMap((check) => check(message))

    return { isValid: errors.length === 0, errors, warnings }
  } catch (error) {
    return { isValid: false, errors: [`Validation error: ${(error as Error).message}`], warnings: [] }
  }
}

/**
 * Filters out invalid translations from language overrides
 */
export function filterValidTranslations(
  languageOverrides: TranslationOverrides | null | undefined
): FilterValidTranslationsResult {
  if (!languageOverrides || typeof languageOverrides !== 'object') {
    return { validOverrides: {}, invalidOverrides: {} }
  }

  const validOverrides: TranslationOverrides = {}
  const invalidOverrides: Record<string, InvalidOverride> = {}

  for (const [key, value] of Object.entries(languageOverrides)) {
    const validation = validateTranslationMessage(value, key)

    if (validation.isValid) {
      validOverrides[key] = value
    } else {
      invalidOverrides[key] = {
        value,
        errors: validation.errors,
        warnings: validation.warnings
      }
    }
  }

  return { validOverrides, invalidOverrides }
}
